import { BiometryKind } from '@ninety/mobile/platform/auth';

/**
 * What to call the authenticator in a sentence, or `null` for "there is nothing to offer".
 *
 * The name lives here rather than on {@link BiometryKind} because the App Lock reports what the OS
 * would raise, and it is the screen's business to decide what that is called in front of a person.
 * The screens own their sentence — "Unlock with …" on the sign-in form, "Require … to open Ninety"
 * in Settings — and take the sensor's name from here, so a new kind is named once.
 *
 * The two platforms want opposite things, which is why this is a table on the kind and not a
 * `platform.is()` check:
 *
 * - **iOS names the sensor exactly.** Apple's guidance is to read the biometry type rather than
 *   infer it, because Touch ID hardware is still shipping — so "Face ID" on an iPhone SE would name
 *   a sensor the device does not have.
 * - **Android stays generic.** A device may carry fingerprint, face and iris at once, and the
 *   framework chooses which one answers, so `BiometricPrompt`'s own guidance is to say "biometrics"
 *   and not guess. The App Lock has already collapsed all of them into one kind.
 *
 * `null` for the two kinds that are not a biometric. `BiometricSignIn.canEnroll` asks with
 * `useFallback: false`, so neither should reach a screen — but one offered under the word "passcode"
 * would be describing something else, and both screens that ask answer "nothing to offer" the same
 * way: by not offering it.
 *
 * `satisfies` rather than a `switch`, so a new kind is a compile error naming the key nobody added
 * rather than an offer that silently stops naming anything.
 */
export const BIOMETRY_NAMES = {
  faceId: 'Face ID',
  touchId: 'Touch ID',
  biometric: 'biometrics',
  deviceCredential: null,
  none: null,
} as const satisfies Record<BiometryKind, string | null>;
